import PropTypes from 'prop-types'
import { Button, Switch } from 'antd'
import {
  CalendarOutlined,
  FileSearchOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import dayjs, { formatDOB } from '../../lib/datetime'
import { reportShowPath } from '../../lib/reports'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import PatientEntry from '../Common/PatientEntry'

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  // Priority switch + view/unassign links share the last line. Left-aligned
  // rather than space-between: the switch label swaps between "Priority" and
  // "Not Priority", so pinning the links right made the edge jitter per row.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
})

// Same formatting as the desktop Cycle column: the date part of the timestamp,
// read as-is so the cycle end doesn't shift a day with the local timezone.
function formatCycleEnd(cycleEndsAt) {
  if (!cycleEndsAt) return null

  return dayjs(cycleEndsAt.split('T').shift()).format('MM/DD/YYYY')
}

// Every desktop column fits on the card, so these rows need no expand toggle.
export default function UnreviewedReportMobileCard({
  report,
  onPriorityChange,
  onUnassign,
}) {
  const classes = useStyles()
  const { assignee, cycleEndsAt, device, patient, practiceName } = report
  // formatDOB(undefined) would render today's date, so only format a real value.
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const deviceLine = [dob, device?.typeName].filter(Boolean).join(' · ')
  const cycleLine = [formatCycleEnd(cycleEndsAt), practiceName]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {(deviceLine || device?.icmCapable) && (
        <div className={classes.line}>
          {deviceLine}
          {device?.icmCapable && (
            <span>
              {deviceLine ? ' · ' : ''}ICM{' '}
              <CircleCheckIcon checked={device.icmActive} />
            </span>
          )}
        </div>
      )}
      {cycleLine && (
        <div className={classes.line}>
          <CalendarOutlined className={classes.lineIcon} />
          {cycleLine}
        </div>
      )}
      {assignee && (
        <div className={classes.line}>
          <UserOutlined className={classes.lineIcon} />
          {assignee.firstName} {assignee.lastName}
        </div>
      )}
      <div className={classes.actionsLine}>
        <Switch
          checked={report.priority}
          checkedChildren="Priority"
          unCheckedChildren="Not&nbsp;Priority"
          onChange={priority => onPriorityChange(report, priority)}
        />
        <Link
          to={{
            pathname: reportShowPath(report),
            search: 'unreviewed=true',
          }}
        >
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            View
          </Button>
        </Link>
        {assignee && (
          <Button
            type="link"
            size="small"
            icon={<StopOutlined />}
            style={{ paddingLeft: 0 }}
            onClick={() => onUnassign(report)}
          >
            Unassign
          </Button>
        )}
      </div>
    </div>
  )
}

UnreviewedReportMobileCard.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.string.isRequired,
    priority: PropTypes.bool,
    cycleEndsAt: PropTypes.string,
    practiceName: PropTypes.string,
    assignee: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    patient: PropTypes.shape({
      birthDate: PropTypes.string,
    }),
    device: PropTypes.shape({
      typeName: PropTypes.string,
      icmCapable: PropTypes.bool,
      icmActive: PropTypes.bool,
    }),
  }).isRequired,
  onPriorityChange: PropTypes.func.isRequired,
  onUnassign: PropTypes.func.isRequired,
}
